import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AreaSelect from '../components/AreaSelect';
import { escortApi, dashboardApi } from '../api/mockApi';
import Avatar from '../components/Avatar';

const QUALIFICATIONS = ['DBS', 'First Aid', 'Safeguard', 'PATS'];
const LICENSE_OPTIONS = ['PCO', 'LOCAL AUTHORITY', 'TAXI BADGE', 'PSV'];
const ROUTE_STATUSES = ['ON ROUTE', 'OFF ROUTE'];

const EMPTY_FILTERS = { name: '', area: '', routeStatus: '', routeName: '' };

const EMPTY_FORM = {
  fullName: '', address: '', postCode: '', area: '', mobileNumber: '',
  qualifications: [], licenses: '', routeStatus: 'OFF ROUTE', attachments: [],
};

const EscortsPage = () => {
  const [escorts, setEscorts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [nameInput, setNameInput] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [options, setOptions] = useState({ areas: [], routeNames: [], statuses: ROUTE_STATUSES });
  const [reloadKey, setReloadKey] = useState(0);
  const navigate = useNavigate();

  const hasFilters = Boolean(filters.name || filters.area || filters.routeStatus || filters.routeName);

  // Filter dropdown options (areas / route names) come from the dashboard filters endpoint.
  useEffect(() => {
    const loadOptions = async () => {
      const res = await dashboardApi.getFilters();
      if (res.success && res.data) {
        setOptions({
          areas: res.data.areas || [],
          routeNames: res.data.routeNames || [],
          statuses: res.data.statuses || ROUTE_STATUSES,
        });
      }
    };
    loadOptions();
  }, []);

  // Debounce the free-text name field (350ms) so we do not hit the API on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => (prev.name === nameInput ? prev : { ...prev, name: nameInput }));
    }, 350);
    return () => clearTimeout(timer);
  }, [nameInput]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setRefreshing(true);
      const res = await escortApi.getAll({
        name: filters.name || undefined,
        area: filters.area || undefined,
        routeStatus: filters.routeStatus || undefined,
        routeName: filters.routeName || undefined,
      });
      if (cancelled) return;
      const rows = res.success && Array.isArray(res.data) ? res.data : [];
      const sorted = [...rows].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setEscorts(sorted);
      if (!hasFilters) setTotal(sorted.length);
      setRefreshing(false);
      setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [filters, reloadKey, hasFilters]);

  const reload = () => setReloadKey(k => k + 1);

  const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  const clearFilters = () => {
    setNameInput('');
    setFilters(EMPTY_FILTERS);
  };

  const openModal = () => {
    setFormError('');
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormError('');
    setForm(EMPTY_FORM);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);
    const res = await escortApi.create(form);
    setSaving(false);
    if (!res || !res.success) {
      setFormError((res && res.error) || 'Could not add this passenger assistant. Please try again.');
      return;
    }
    setShowModal(false);
    setForm(EMPTY_FORM);
    reload();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this passenger assistant?')) {
      await escortApi.delete(id);
      reload();
    }
  };

  const toggleQual = (q) => {
    setForm(prev => ({
      ...prev,
      qualifications: prev.qualifications.includes(q)
        ? prev.qualifications.filter(x => x !== q)
        : [...prev.qualifications, q],
    }));
  };

  if (loading) return <div className="loading">Loading passenger assistants...</div>;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Passenger Assistants ({escorts.length})</h1>
        <button className="btn btn-gold" onClick={openModal}>+ Add PA</button>
      </div>

      <div className="filters-bar">
        <div className="filter-field">
          <label>Name</label>
          <input
            value={nameInput}
            onChange={e => setNameInput(e.target.value)}
            placeholder="Search by name"
          />
        </div>
        <div className="filter-field">
          <label>Area</label>
          <select value={filters.area} onChange={e => setFilter('area', e.target.value)}>
            <option value="">All areas</option>
            {options.areas.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="filter-field">
          <label>Route</label>
          <select value={filters.routeName} onChange={e => setFilter('routeName', e.target.value)}>
            <option value="">All routes</option>
            {options.routeNames.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="filter-field">
          <label>Route Status</label>
          <select value={filters.routeStatus} onChange={e => setFilter('routeStatus', e.target.value)}>
            <option value="">All</option>
            {options.statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="filters-actions">
          <button className="btn btn-outline btn-sm" onClick={clearFilters} disabled={!hasFilters && !nameInput}>
            Clear filters
          </button>
        </div>
        <div className="filter-summary">
          {refreshing
            ? 'Loading...'
            : hasFilters
              ? `Showing ${escorts.length} of ${total} passenger assistants`
              : `${escorts.length} passenger assistant${escorts.length === 1 ? '' : 's'}`}
        </div>
      </div>

      <table className="data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Area</th>
            <th>Mobile</th>
            <th>Qualifications</th>
            <th>Route</th>
            <th>Assigned Driver</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {escorts.map(e => (
            <tr key={e.id}>
              <td style={{ cursor: 'pointer', color: '#DBAD17' }} onClick={() => navigate(`/escorts/${e.id}`)}>
                <div className="name-cell">
                  <Avatar name={e.fullName} photoUrl={e.photoUrl} size={34} />
                  <span>{e.fullName || 'N/A'}</span>
                </div>
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>{e.area || '—'}</td>
              <td style={{ whiteSpace: 'nowrap' }}>{e.mobileNumber}</td>
              <td>
                <div className="qual-badges" style={{ flexWrap: 'nowrap' }}>
                  {QUALIFICATIONS.map(q => (
                    <span key={q} className={`qual-badge ${e.qualifications?.includes(q) ? 'active' : 'inactive'}`} style={{ fontSize: 11, padding: '3px 8px' }}>
                      {q}
                    </span>
                  ))}
                </div>
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>{e.routeName || '—'}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {e.belongsToDriverName || <span className="pill">Unassigned</span>}
              </td>
              <td>
                <span className={`status-badge ${e.routeStatus === 'ON ROUTE' ? 'on-route' : 'off-route'}`}>
                  <span className="dot"></span>
                  {e.routeStatus}
                </span>
              </td>
              <td>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-outline btn-sm" onClick={() => navigate(`/escorts/${e.id}`)}>View</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(e.id)}>Delete</button>
                </div>
              </td>
            </tr>
          ))}
          {!escorts.length && (
            <tr>
              <td colSpan={8}>
                <div className="empty-state">
                  {hasFilters ? 'No passenger assistants match these filters.' : 'No passenger assistants yet.'}
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Add New Passenger Assistant</h3>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>Full Name *</label>
                <input value={form.fullName} onChange={e => setForm({...form, fullName: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Mobile Number *</label>
                <input value={form.mobileNumber} onChange={e => setForm({...form, mobileNumber: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input value={form.address} onChange={e => setForm({...form, address: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Post Code</label>
                <input value={form.postCode} onChange={e => setForm({...form, postCode: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Area</label>
                <AreaSelect
                  value={form.area || ''}
                  onChange={v => setForm({ ...form, area: v })}
                  blankLabel="Detect from post code"
                />
              </div>
              <div className="form-group">
                <label>Qualifications</label>
                <div className="checkbox-group">
                  {QUALIFICATIONS.map(q => (
                    <div key={q} className={`checkbox-item ${form.qualifications.includes(q) ? 'checked' : ''}`} onClick={() => toggleQual(q)}>
                      {form.qualifications.includes(q) ? '✓' : '○'} {q}
                    </div>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label>License</label>
                <select value={form.licenses} onChange={e => setForm({...form, licenses: e.target.value})}>
                  <option value="">Select license</option>
                  {LICENSE_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Route Status</label>
                <select value={form.routeStatus} onChange={e => setForm({...form, routeStatus: e.target.value})}>
                  <option value="ON ROUTE">ON ROUTE</option>
                  <option value="OFF ROUTE">OFF ROUTE</option>
                </select>
              </div>
              {formError && <p className="error-text" style={{ textAlign: 'left' }}>{formError}</p>}

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-gold" disabled={saving}>
                  {saving ? 'Adding...' : 'Add PA'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EscortsPage;
